import { useEffect, useState } from "react"

import { isNaN } from 'lodash'
import styled from "styled-components"


import { AnnotationComment } from "./AnnotationComment"
import { CanvasAnnotations } from "./CanvasAnnotations"
import { ANNOTATION_MODE, CREATING_NEW_ANNOTATION_MODE } from './utils/annotationUtils'

export const getAnnotationAction = (type: string): ANNOTATION_MODE => {
  switch (type) {
  case 'RECTANGLE':
    return ANNOTATION_MODE.RECTANGLE
  case 'POLYGON':
    return ANNOTATION_MODE.POLYGON
  case 'CIRCLE':
    return ANNOTATION_MODE.CIRCLE
  case 'POINT':
    return ANNOTATION_MODE.POINT
  default:
    return ANNOTATION_MODE.SELECT
  }
}

export enum ANNOTATION_COMMENT_ACTION {
  ADD = 'ADD',
  EDIT = 'EDIT',
  DELETE = 'DELETE',
  OPEN_REPLY = 'OPEN_REPLY',
  RESOLVE = 'RESOLVE',
}

interface AssetAnnotationsProps {
  stageMargin: number
  className?: string
  parentRef: React.RefObject<HTMLDivElement>,
  assetRef: React.RefObject<any>,
  assetBoundingBox: { width: number, height: number }
  isCanvasVisible: boolean
  isCommentVisible: boolean
  currentZoom?: number
  position: { x: number, y: number }
  isDragging: boolean
  previewUrl?: string
  annotations: any[]
  creatingNewAnnotationMode: CREATING_NEW_ANNOTATION_MODE | null
  onAnnotationSelect?: (annotation: any) => void
  onAnnotationSubmit?: (annotation: any) => void
  onAnnotationClick?: (annotation: any) => void
  onFinishImgLoading?: () => void
  onErrorImgLoading?: () => void
  onActiveAnnotation?: (annotation: any) => void
  onReplyToComment?: (annotation: any) => void
  newAnnotation: any
  selectedAnnotation: any
  onCreateNewAnnotation: (annotation: any) => void
  onStartCreatingNewAnnotation?: () => void
  onFinishCreatingNewAnnotation?: () => void
  onClearNewAnnotation: () => void
  showMarkers?: boolean
  onCommentClose: () => void
  onCommentAction: (action: ANNOTATION_COMMENT_ACTION, data: any) => void
  boundingRect?: { left: number, top: number, right: number, bottom: number }
  containerOffset?: { x: number, y: number }
  assetContentSize?: {width: number, height: number, containerOffset?: { x: number, y: number }}
  onCanvasClickOutside?: () => void
}

export const AssetAnnotations = ({
  assetContentSize,
  stageMargin,
  className,
  parentRef,
  assetRef,
  assetBoundingBox,
  isCanvasVisible,
  isCommentVisible,
  currentZoom = 100,
  position,
  isDragging,
  previewUrl = '',
  annotations,
  creatingNewAnnotationMode,
  onAnnotationSelect,
  onAnnotationSubmit,
  onAnnotationClick,
  onFinishImgLoading,
  onErrorImgLoading,
  onActiveAnnotation,
  newAnnotation,
  selectedAnnotation,
  onCreateNewAnnotation,
  onStartCreatingNewAnnotation,
  onFinishCreatingNewAnnotation,
  onClearNewAnnotation,
  onReplyToComment,
  showMarkers,
  onCommentClose,
  onCommentAction,
  boundingRect,
  containerOffset = { x: 0, y: 0 },
  onCanvasClickOutside,
}: AssetAnnotationsProps) => {
  const [extendedAnnotations, setExtendedAnnotations] = useState([])
  const [extendedNewAnnotation, setExtendedNewAnnotation] = useState(null)
  const [extendedSelectedAnnotation, setExtendedSelectedAnnotation] = useState(null)

  const filterAnnotations = (annotation: any) => {
    const coordinates = annotation.data.annotation.coordinates
    return coordinates && coordinates.length >= 2 && coordinates[0] !== -1 && coordinates[1] !== -1
  }

  useEffect(() => {
    const { width, height } = assetBoundingBox
    const assetWidth = Number(width)
    const assetHeight = Number(height)
    if (isNaN(assetWidth) && isNaN(height)) return
    const annotationsWithCoordinates = annotations.filter(filterAnnotations)
    setExtendedAnnotations(annotationsWithCoordinates.map((annotation) => {
      const absX = annotation.x * assetWidth / 100 + stageMargin / 2
      const absY = annotation.y * assetHeight / 100 + stageMargin / 2
      const absWidth = annotation.width * assetWidth / 100
      const absHeight = annotation.height * assetHeight / 100
      return {
        ...annotation,
        geometry: {
          absolute: {
            x: absX,
            y: absY,
            width: absWidth,
            height: absHeight,
          },
          relative: {
            x: annotation.x,
            y: annotation.y,
            width: annotation.width,
            height: annotation.height,
          }
        }
      }
    }))
  }, [annotations, assetBoundingBox])

  useEffect(() => {
    if (newAnnotation) {
      const { x, y, width, height } = newAnnotation
      const { width: assetWidth, height: assetHeight } = assetBoundingBox
      const relativeGeometry = { x: x * 100 / assetWidth , y: y * 100 / assetHeight, width: width * 100 / assetWidth, height: height * 100 / assetHeight }
      setExtendedNewAnnotation({
        ...newAnnotation,
        geometry: {
          relative: relativeGeometry,
          absolute: { x, y, width, height },
        }
      })
      return
    }
    setExtendedNewAnnotation(newAnnotation)
  }, [newAnnotation, assetBoundingBox])

  useEffect(() => {
    if (selectedAnnotation) {
      const { x, y, width, height } = selectedAnnotation.geometry.relative
      const { width: assetWidth, height: assetHeight } = assetBoundingBox
      const absolute = { x: x * assetWidth / 100, y: y * assetHeight / 100, width: width * assetWidth / 100, height: height * assetHeight / 100 }
      setExtendedSelectedAnnotation({
        ...selectedAnnotation,
        geometry: {
          relative: { x, y, width, height },
          absolute,
        }
      })
      return
    }
    setExtendedSelectedAnnotation(selectedAnnotation)
  }, [selectedAnnotation, assetBoundingBox])

  return (
    <SAssetAnnotationsContainer
      className={className}
    >
      <SCanvasAnnotations
        parentRef={parentRef}
        assetRef={assetRef}
        stageMargin={stageMargin}
        className="canvas-annotations"
        isVisible={isCanvasVisible}
        position={position}
        isDragging={isDragging}
        assetBoundingBox={assetBoundingBox}
        currentZoom={currentZoom}
        previewUrl={previewUrl}
        annotations={extendedAnnotations}
        showMarkers={showMarkers}
        newAnnotation={extendedNewAnnotation}
        selectedAnnotation={extendedSelectedAnnotation}
        creatingNewAnnotationMode={creatingNewAnnotationMode}
        onAnnotationSelect={onAnnotationSelect}
        onAnnotationSubmit={onAnnotationSubmit}
        onCreateNewAnnotation={onCreateNewAnnotation}
        onStartCreatingNewAnnotation={onStartCreatingNewAnnotation}
        onFinishCreatingNewAnnotation={onFinishCreatingNewAnnotation}
        onClearNewAnnotation={onClearNewAnnotation}
        onFinishImgLoading={onFinishImgLoading}
        onErrorImgLoading={onErrorImgLoading}
        onActiveAnnotation={onActiveAnnotation}
        onAnnotationClick={onAnnotationClick}
        onCanvasClickOutside={onCanvasClickOutside}
      />

      <SAnnotationComment
        assetContentSize={assetContentSize || null}
        assetRef={parentRef}
        isVisible={isCommentVisible}
        stageMargin={stageMargin}
        assetBoundingBox={assetBoundingBox}
        boundingRect={boundingRect}
        containerOffset={containerOffset}
        newAnnotation={extendedNewAnnotation}
        selectedAnnotation={extendedSelectedAnnotation}
        currentZoom={currentZoom}
        assetPosition={position}
        onClose={onCommentClose}
        onAction={onCommentAction}
        onReplyToComment={onReplyToComment}
      />
    </SAssetAnnotationsContainer>
  )
}


const SAssetAnnotationsContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`

const SCanvasAnnotations = styled(CanvasAnnotations)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  justify-content: center;
  align-items: center;
`

const SAnnotationComment = styled(AnnotationComment)`
  user-select: text;
`
